import _ from 'lodash';
import React from 'react';

const SearchBarContext = React.createContext();

function useLockFocus() {
  const { dispatch } = React.useContext(SearchBarContext);
  return {
    onMouseDown: () => dispatch({ type: 'focus', payload: true }),
  };
}

const caretDown = s => {
  const result = { ...s, caret: -1 };
  const len = s.rsearch.length + s.suggestions.length;
  if (len) {
    if (s.caret === len - 1) {
      result.value = s.query || '';
    } else {
      result.caret = (s.caret + 1) % len;
    }
  }
  return result;
};

const caretUp = s => {
  const result = { ...s, caret: -1 };
  const len = s.rsearch.length + s.suggestions.length;
  if (len) {
    if (s.caret === -1) {
      result.caret = len - 1;
    } else if (s.caret) {
      result.caret = (s.caret + len - 1) % len;
    } else {
      result.value = s.query || '';
    }
  }
  return result;
};

const maxWidth = 576;
const isPseudoMobile = (s, w) => !!(s.isMobile || w < maxWidth);

const typedResultPageURL = (state, payload) => {
  const type = _.get(state, 'suggestedSearchItem.type');
  const query = encodeURIComponent(payload || state.value);

  switch (type) {
    case 'Wire':
      return `/search/wires?query=${query}`;
    case 'Contributor':
      return `/search/contributor/${_.get(state, 'suggestedSearchItem.slug')}`;
    case 'Organisation':
      return `/search/organisation/${encodeURIComponent(_.get(state, 'suggestedSearchItem.suggested_term') || query)}`;
    case 'Company':
      return `/search/listed_company/${_.get(state, 'suggestedSearchItem.slug')}`;
    case 'Keyword':
      return `/search/keyword/${_.get(state, 'suggestedSearchItem.slug')}`;
    default:
      return `/search/text/${query}`;
  }
};

// eslint-disable-next-line complexity
const reducer = (state, { type, payload }) => {
  const animation = { animation: state.width !== 0 };
  const ssct = { showStockCodeTick: false };
  switch (type) {
    case 'expand':
      return {
        ...state,
        ...animation,
        ...ssct,
        expanded: !!payload,
      };
    case 'setValue':
      return {
        ...state,
        ...animation,
        ...ssct,
        value: payload || '',
      };
    case 'setSuggestedSearchItem':
      return { ...state, suggestedSearchItem: payload || null };
    case 'setQuery':
      return {
        ...state,
        showSuggestion: _.get(payload, 'length') >= 3,
        value: payload || '',
        query: payload,
        ...animation,
        ...ssct,
      };
    case 'setWidth':
      return { ...state, width: payload, pseudoMobile: isPseudoMobile(state, payload) };
    case 'focus':
      return { ...state, focus: payload, ...animation };
    case 'inputFocus':
      return { ...state, inputFocus: payload, ...animation };
    case 'setRecentSearchResult':
      // always reset the highlight caret when new result comes in
      return { ...state, rsearch: payload, caret: -1 };
    case 'setSuggestionResult':
      // always reset the highlight caret when new result comes in
      return { ...state, suggestions: payload, caret: -1 };
    case 'setHighlightedArea':
      return { ...state, highlightedArea: payload };
    case 'resetCaret':
      return { ...state, caret: -1, value: state.query };
    case 'arrowDown':
      return caretDown(state);
    case 'arrowUp':
      return caretUp(state);
    case 'search':
      // To prevent double clicks on Search buttons
      if (!state.performingSearch) {
        window.location = typedResultPageURL(state, payload);
      }

      return { ...state, performingSearch: true };
    default:
      throw new Error();
  }
};

const searchPathRegExp = /^\/search\/(wire|contributor|organisation|listed_company)($|\/)/;

const isOnSearchResultsPage = window.location.pathname.match(searchPathRegExp);

const typedQueryValue = () => {
  if (!isOnSearchResultsPage) {
    return { value: '' };
  }

  // The if condition above guarantees a match here
  const [, type] = isOnSearchResultsPage;
  const query = _.get(window, 'Livewire.request.searchQuery.query');
  const value = query ? _.unescape(query) : '';
  return { type, value };
};

const sQuery = typedQueryValue();

const defaultState = {
  animation: false,
  expanded: !!isOnSearchResultsPage,
  width: 0,
  value: sQuery.value,
  showStockCodeTick: sQuery.type === 'listed_company',
  query: '',
  showRecent: false,
  rsearch: [],
  showSuggestion: false,
  suggestions: [],
  caret: -1,
  highlightedArea: null,
  focus: false,
  inputFocus: false,
  suggestedSearchItem: null,
  performingSearch: false,
};

export default SearchBarContext;
export {
  useLockFocus,
  reducer,
  defaultState,
};
